/**
 * ReopenDialog — reopen a resolved demand. Clears the resolution (and any merge
 * link) and sends it back to Under review to be triaged from scratch. A reason
 * is required and recorded in the stage history. A merged demand shows a warning
 * that reopening un-merges it.
 */

import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Select,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { useReopenDemand } from '../hooks/useDemands'
import { apiErrorMessage } from '../lib/apiError'
import type { Demand } from '../types/demand'

// The active (non-resolved) stages a reopen can target. Labels match lib/labels.
// Reopen may only land in Scoping or Triaging (v2.8.0): Resourcing is
// re-entered via Triaging, New is entry-only, Parking is a suspend not a reopen.
const REOPEN_STAGES: { key: string; label: string }[] = [
  { key: 'scoping', label: 'Scoping' },
  { key: 'triaging', label: 'Triaging' },
]

const useStyles = makeStyles({
  surface: { maxWidth: '480px', width: '100%' },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  hint: { fontSize: '13px', color: tokens.colorNeutralForeground2 },
  warn: {
    fontSize: '13px',
    color: tokens.colorPaletteDarkOrangeForeground1,
    background: tokens.colorPaletteDarkOrangeBackground1,
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
  },
  error: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
  notes: { width: '100%' },
})

export interface ReopenDialogProps {
  open: boolean
  demand: Demand
  onClose: () => void
  onReopened?: (demand: Demand) => void
}

export function ReopenDialog({ open, demand, onClose, onReopened }: ReopenDialogProps) {
  const styles = useStyles()
  const [reason, setReason] = useState('')
  const [toStage, setToStage] = useState('scoping')
  const [error, setError] = useState<string | null>(null)
  const mutation = useReopenDemand()
  const isPending = mutation.isPending

  useEffect(() => {
    if (open) {
      setReason('')
      setToStage('scoping')
      setError(null)
    }
  }, [open, demand.id])

  const trimmed = reason.trim()
  const canConfirm = trimmed.length > 0 && !isPending
  const wasMerged = demand.resolution_type === 'merged'

  function handleConfirm() {
    if (!canConfirm) return
    setError(null)
    mutation.mutate(
      { id: demand.id, reason: trimmed, toStage },
      {
        onSuccess: (d) => {
          onReopened?.(d)
          onClose()
        },
        onError: (err: unknown) => {
          setError(apiErrorMessage(err, 'Failed to reopen demand'))
        },
      },
    )
  }

  function handleOpenChange(_e: unknown, data: { open: boolean }) {
    if (!data.open && !isPending) onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="modal">
      <DialogSurface className={styles.surface} aria-label="Reopen demand">
        <DialogBody>
          <DialogTitle>Reopen demand</DialogTitle>
          <DialogContent className={styles.body}>
            <div className={styles.hint}>
              This clears the resolution and moves the demand to the stage you choose.
            </div>
            {wasMerged && (
              <div className={styles.warn} data-testid="reopen-merge-warning">
                This demand was <strong>merged</strong> into another. Reopening will{' '}
                <strong>un-merge</strong> it.
              </div>
            )}
            <Field label="Reopen to">
              <Select
                value={toStage}
                onChange={(_, d) => setToStage(d.value)}
                data-testid="reopen-stage"
              >
                {REOPEN_STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Textarea
              className={styles.notes}
              placeholder="Reason for reopening (recorded in the stage history)…"
              value={reason}
              onChange={(_, d) => setReason(d.value)}
              resize="vertical"
              data-testid="reopen-reason"
            />
            {error && <div className={styles.error}>{error}</div>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="reopen-confirm"
            >
              {isPending ? 'Reopening…' : 'Reopen'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
